// Shared draft mirror for document editors (invoice, bill). The editor lives
// in a focus drawer; closing it must not discard an in-progress document, so
// the draft is lifted to the page and mirrored into this browser session per
// document kind and organization. It is a convenience mirror only — the API
// remains the source of truth once created.
import type { DocumentLineDraft } from "./productDetails";

export interface DraftLine extends DocumentLineDraft {
  uid: string;
}

export interface DocumentDraft {
  partyName: string;
  issueDate: string;
  /** Invoice/bill due date, or quote expiry date. */
  dueAt: string;
  paymentTermId: string;
  crmContactId: string;
  lines: DraftLine[];
}

export function todayInput(): string {
  return new Date().toISOString().slice(0, 10);
}

export function emptyDraftLine(): DraftLine {
  return {
    uid: crypto.randomUUID(),
    account_id: "",
    description: "",
    amount: "",
    tax_rate_id: "",
    sku_id: "",
    quantity: "",
    unit: "",
    unit_price: "",
  };
}

export function newDocumentDraft(): DocumentDraft {
  return {
    partyName: "",
    issueDate: todayInput(),
    dueAt: "",
    paymentTermId: "",
    crmContactId: "",
    lines: [emptyDraftLine()],
  };
}

function draftKey(prefix: string, organizationId: string): string {
  return `${prefix}:${organizationId}`;
}

function isLineDraft(value: unknown): value is DraftLine {
  if (typeof value !== "object" || value === null) return false;
  const line = value as Record<string, unknown>;
  return (
    typeof line.uid === "string" &&
    typeof line.account_id === "string" &&
    typeof line.description === "string" &&
    typeof line.amount === "string"
  );
}

function stringOr(value: unknown, fallback: string): string {
  return typeof value === "string" ? value : fallback;
}

export function loadDocumentDraft(
  prefix: string,
  organizationId: string,
): DocumentDraft | null {
  try {
    const raw = window.sessionStorage.getItem(draftKey(prefix, organizationId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const draft = parsed as Record<string, unknown>;
    const lines = Array.isArray(draft.lines)
      ? draft.lines.filter(isLineDraft)
      : [];
    return {
      partyName: stringOr(draft.partyName, ""),
      issueDate: stringOr(draft.issueDate, "") || todayInput(),
      dueAt: stringOr(draft.dueAt, ""),
      paymentTermId: stringOr(draft.paymentTermId, ""),
      crmContactId: stringOr(draft.crmContactId, ""),
      lines: lines.length > 0 ? lines : [emptyDraftLine()],
    };
  } catch {
    return null;
  }
}

export function saveDocumentDraft(
  prefix: string,
  organizationId: string,
  draft: DocumentDraft,
): void {
  try {
    window.sessionStorage.setItem(
      draftKey(prefix, organizationId),
      JSON.stringify(draft),
    );
  } catch {
    // Session storage blocked or full — the in-memory draft still works.
  }
}

export function clearDocumentDraft(prefix: string, organizationId: string): void {
  try {
    window.sessionStorage.removeItem(draftKey(prefix, organizationId));
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}
